import React from "react";

import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";

import Actions from "./Actions";
import Details from "./Details";
import InvoicePayments from "./InvoicePayments";
import InvoiceModals from "../InvoiceModals";
import InvoiceDetailsTabs, { DetailsTabId } from "./InvoiceDetailsTabs";
import DetailsSkeleton from "./Details/DetailsSkeleton";

import { useInvoiceDetails } from "./useInvoiceDetails";
import { formatIban } from "shared/functions/formatIban";

import { ReactComponent as ArrowIcon } from "assets/images/sideMenu/arrow-icon.svg";

import { styles } from "./styles";

const InvoiceDetails: React.FC = () => {
  const {
    invoice,
    payments,
    pageCount,
    pageNumber,
    isLoading,
    visibleTab,
    setVisibleTab,
    handleGoBack,
    onPageChange,
    onPaymentStatusChange,
    onAfterUpdateSubmit,
    onAfterDeleteSubmit,
  } = useInvoiceDetails();

  return (
    <Stack width="100%">
      <Link component="button" onClick={handleGoBack} sx={styles.backLink}>
        <ArrowIcon style={{ transform: "rotate(180deg)" }} />
        Назад
      </Link>
      <Stack sx={styles.header}>
        <Box sx={styles.titleWrapper}>
          <Typography sx={{ mb: 0.5 }} variant="h3">
            Деталі посилання
          </Typography>
          <Typography variant="body1" color="grey700">
            Загальна інформація та аналітика
          </Typography>
        </Box>

        <Actions invoice={invoice} />
      </Stack>

      <InvoiceDetailsTabs
        visibleTab={visibleTab}
        setVisibleTab={setVisibleTab}
      />

      <Box
        data-testid="invoice-details-container"
        sx={{
          mb: 3,
          display: visibleTab === DetailsTabId.Details ? "block" : "none",
        }}
      >
        {isLoading ? <DetailsSkeleton /> : <Details invoice={invoice} />}
      </Box>

      <Box
        data-testid="invoice-payments-container"
        sx={{
          display: visibleTab === DetailsTabId.Payments ? "block" : "none",
        }}
      >
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" sx={{ mb: 0.5 }}>
            Історія оплат
          </Typography>

          {isLoading ? (
            <>
              <Skeleton
                variant="text"
                width="60%"
                height={24}
                sx={{ mb: 0.5 }}
              />
              <Skeleton
                variant="text"
                width="40%"
                height={14}
                sx={{ mb: 0.5 }}
              />
            </>
          ) : (
            <>
              <Typography color="grey700" sx={{ mb: 0.5 }}>
                {invoice?.organizationName || ""}
              </Typography>
              <Typography variant="subtitle2" color="grey700">
                {formatIban(invoice?.iban || "")}
              </Typography>
            </>
          )}
        </Box>

        <InvoicePayments
          loading={isLoading}
          payments={payments}
          pageCount={pageCount}
          pageNumber={pageNumber}
          onPageChange={onPageChange}
          onStatusChange={onPaymentStatusChange}
        />
      </Box>

      <InvoiceModals
        onAfterUpdateSubmit={onAfterUpdateSubmit}
        onAfterDeleteSubmit={onAfterDeleteSubmit}
      />
    </Stack>
  );
};

export default InvoiceDetails;
